import React, { useState } from "react";
import * as Mui from "@mui/material";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { convertDurationUnit } from "services/utils/durationUnits";
import {
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";

// Shared building blocks for "settings form" pages (a page of numbered
// sections with Reset to Default / Save Changes at the foot). First used
// by Administration > System Punch Processing.

export const RequiredMark = () => (
  <Mui.Box component="span" sx={{ color: "#DC2626", ml: 0.5 }}>
    *
  </Mui.Box>
);

// Page chrome: breadcrumb, title, subtitle, the two footer actions and the
// "discard my changes?" confirmation for Reset to Default.
export const SettingsPageShell = ({
  breadcrumb,
  title,
  subtitle,
  notice,
  isDirty,
  isSaving = false,
  onSave,
  onReset,
  resetMessage = "This discards your changes and restores the default settings.",
  // View-only users see the saved values but cannot change or save them.
  readOnly = false,
  children,
}) => {
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <Mui.ThemeProvider theme={designTheme}>
      <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb trail={breadcrumb} />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              {title}
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>{subtitle}</Mui.Typography>
          </Mui.Box>
          {notice}
          {/* `inert` also keeps the keyboard out of controls a disabled
              fieldset does not cover (MUI selects, switches). */}
          <Mui.Box
            component="fieldset"
            disabled={isSaving || readOnly}
            {...(readOnly ? { inert: "" } : {})}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0, ...(readOnly ? { pointerEvents: "none" } : {}) }}
          >
            <Mui.Stack spacing={2}>{children}</Mui.Stack>
          </Mui.Box>
          {/* Page actions sit at the foot of the form, right-aligned, the
              same placement as Organisation > Details and Settings. */}
          {!readOnly && <Mui.Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            justifyContent="flex-end"
            spacing={1.5}
          >
            <SecondaryButton
              startIcon={<RestartAltRounded />}
              onClick={() => setResetOpen(true)}
              disabled={isSaving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              Reset to Default
            </SecondaryButton>
            <PrimaryButton
              startIcon={<SaveOutlined />}
              onClick={onSave}
              disabled={!isDirty || isSaving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </PrimaryButton>
          </Mui.Stack>}
        </Mui.Stack>
      </Mui.Box>

      <GlobalConfirmDialog
        open={resetOpen}
        title="Reset to default"
        onCancel={() => setResetOpen(false)}
        onConfirm={() => {
          setResetOpen(false);
          onReset();
        }}
        confirmLabel="Reset"
        cancelLabel="Cancel"
        danger
      >
        {resetMessage}
      </GlobalConfirmDialog>
    </Mui.ThemeProvider>
  );
};

const unitBoxSx = {
  width: 96,
  minWidth: 96,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: `0 ${designRadii.control} ${designRadii.control} 0`,
    backgroundColor: designColors.slateBg,
  },
};

// A duration input joined to its unit. The unit is a dropdown when
// `unitOptions` offers a choice, otherwise a fixed read-only label.
//   value: { value: "2", unit: "minutes" }
export const DurationField = ({
  label,
  required = false,
  value,
  onChange,
  unitOptions = [],
  helperText,
  error,
  min = 1,
  max,
}) => {
  const hasUnitChoice = unitOptions.length > 1;
  return (
    <Mui.Box>
      <GlobalFieldLabel>
        {label}
        {required && <RequiredMark />}
      </GlobalFieldLabel>
      <Mui.Stack direction="row" sx={{ maxWidth: 380 }}>
        <Mui.TextField
          type="number"
          size="small"
          value={value.value}
          onChange={(event) => {
            const next = event.target.value;
            onChange({ ...value, value: max != null && next !== "" && Number(next) > max ? String(max) : next });
          }}
          error={Boolean(error)}
          inputProps={{ min: hasUnitChoice && value.unit === "minutes" ? 1 / 60 : min, max, step: hasUnitChoice && value.unit === "minutes" ? "any" : 1, "aria-label": label }}
          sx={{
            ...globalTextFieldSx,
            flex: 1,
            minWidth: 72,
            "& .MuiOutlinedInput-root": {
              backgroundColor: designColors.white,
              borderRadius: `${designRadii.control} 0 0 ${designRadii.control}`,
            },
          }}
        />
        {hasUnitChoice ? (
          <Mui.TextField
            select
            size="small"
            value={value.unit}
            onChange={(event) => onChange(convertDurationUnit(value, event.target.value))}
            error={Boolean(error)}
            inputProps={{ "aria-label": `${label} unit` }}
            sx={{ ...unitBoxSx, ml: "-1px" }}
          >
            {unitOptions.map((option) => (
              <Mui.MenuItem key={option} value={option}>
                {option}
              </Mui.MenuItem>
            ))}
          </Mui.TextField>
        ) : (
          <Mui.Box
            sx={{
              width: 96,
              minWidth: 96,
              boxSizing: "border-box",
              flexShrink: 0,
              ml: "-1px",
              px: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              color: designColors.slate700,
              backgroundColor: designColors.slateBg,
              border: `1px solid ${error ? "#DC2626" : designColors.slate300}`,
              borderRadius: `0 ${designRadii.control} ${designRadii.control} 0`,
            }}
          >
            {value.unit}
          </Mui.Box>
        )}
      </Mui.Stack>
      {(error || helperText) && (
        <Mui.FormHelperText error={Boolean(error)} sx={{ mt: 0.75, fontSize: 12.5 }}>
          {error || helperText}
        </Mui.FormHelperText>
      )}
    </Mui.Box>
  );
};

export default SettingsPageShell;
